import React, { useEffect, useState } from "react";
import { getBookingDetails, updateGuestDetails } from "../api-client";
import { BookingResponse, PassengerDetails } from "../../../backend/src/models/bookingDetails";

interface UpdateGuestDetailsProps {
    closeModal: () => void;
    bookingResponseToken: string;
    onGuestDetailsUpdated: () => void;
}

const UpdateGuestDetailsForm: React.FC<UpdateGuestDetailsProps> = ({ closeModal, bookingResponseToken, onGuestDetailsUpdated }) => {
    const [guests, setGuests] = useState<PassengerDetails[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchDetails();
    }, [bookingResponseToken]);

    const fetchDetails = async () => {
        try {
            setIsLoading(true);
            const data: BookingResponse = await getBookingDetails(bookingResponseToken);
    
            // Extract guests from booking data based on paxGroups
            const extractedGuests: PassengerDetails[] = data.paxGroups.flatMap(group =>
                group.guestDetails.map(guest => ({
                    id: guest.id,
                    primary: guest.primary,
                    type: guest.type,
                    wheelChair: guest.wheelChair ?? false,
                    firstName: guest.firstName ?? "",
                    lastName: guest.lastName ?? "",
                    childAge: guest.type === "child" ? guest.age : undefined,
                    arrivalFlightDate: "",
                    arrivalFlightAirline: guest.flightDetails.find(f => f.type === "arrival")?.flightNo || "",
                    arrivalFlightNumber: guest.flightDetails.find(f => f.type === "arrival")?.flightNo || "",
                    departureFlightDate: "",
                    departureFlightAirline: guest.flightDetails.find(f => f.type === "departure")?.flightNo || "",
                    departureFlightNumber: guest.flightDetails.find(f => f.type === "departure")?.flightNo || "",
                }))
            );
    
            setGuests(extractedGuests);
        } catch (error) {
            setError("Failed to fetch booking details. Please try again later.");
            console.error("Error fetching booking details:", error);
        } finally {
            setIsLoading(false);
        }
    };
    

    const handleChange = (index: number, field: string, value: any) => {
        const updatedGuests = [...guests];
        updatedGuests[index] = { ...updatedGuests[index], [field]: value };
        setGuests(updatedGuests);
    };

    const handleSubmit = async () => {
        try {
            setIsLoading(true);
            setError(null);
    
            // Wrap the guests array in an object with a `details` key
            await updateGuestDetails(bookingResponseToken, guests);
    
            alert("Guest details updated successfully.");
            onGuestDetailsUpdated();
            closeModal(); 
        } catch (error) {
            setError("Failed to update guest details. Please try again later.");
            console.error("Error updating guest details:", error);
        } finally {
            setIsLoading(false);
        }
    };
    

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="relative bg-white rounded-lg shadow-lg md:w-[60%] h-auto max-h-[90%] overflow-auto p-6">
        <button
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            onClick={closeModal}
            aria-label="Close Modal"
        >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>
        <h1 className="text-3xl font-bold mb-6 text-gray-800 text-center">Update Guest Details</h1>

        {error && <p className="text-red-500 mb-4 text-center">{error}</p>}

        {guests.map((guest, index) => (
            <div key={index} className="bg-gray-50 p-6 mb-6 rounded-lg shadow-md">
                <h2 className="text-xl font-semibold mb-4 text-gray-700">
                    Guest {index + 1} {guest.primary ? "(Primary)" : ""}
                </h2>
                <div className="grid grid-cols-2 gap-4">
                    <input
                        type="text"
                        placeholder="First Name"
                        value={guest.firstName}
                        onChange={(e) => handleChange(index, "firstName", e.target.value)}
                        className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <input
                        type="text"
                        placeholder="Last Name"
                        value={guest.lastName}
                        onChange={(e) => handleChange(index, "lastName", e.target.value)}
                        className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <input
                        type="text"
                        placeholder="Type (adult/child)"
                        value={guest.type}
                        onChange={(e) => handleChange(index, "type", e.target.value)}
                        className="p-3 border rounded-lg bg-gray-100"
                        disabled
                    />
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={guest.wheelChair}
                            onChange={(e) => handleChange(index, "wheelChair", e.target.checked)}
                            className="mr-2 focus:ring-2 focus:ring-blue-500"
                        />
                        Requires Wheelchair
                    </label>
                    {guest.type === "child" && (
                        <input
                            type="number"
                            placeholder="Child Age"
                            value={guest.childAge || ""}
                            onChange={(e) => handleChange(index, "childAge", e.target.value)}
                            className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                    )}
                    <input
                        type="date"
                        value={guest.arrivalFlightDate || ""}
                        onChange={(e) => handleChange(index, "arrivalFlightDate", e.target.value)}
                        className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <input
                        type="text"
                        placeholder="Arrival Flight Airline"
                        value={guest.arrivalFlightAirline}
                        onChange={(e) => handleChange(index, "arrivalFlightAirline", e.target.value)}
                        className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <input
                        type="text"
                        placeholder="Arrival Flight Number"
                        value={guest.arrivalFlightNumber}
                        onChange={(e) => handleChange(index, "arrivalFlightNumber", e.target.value)}
                        className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <input
                        type="date"
                        value={guest.departureFlightDate || ""}
                        onChange={(e) => handleChange(index, "departureFlightDate", e.target.value)}
                        className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <input
                        type="text"
                        placeholder="Departure Flight Airline"
                        value={guest.departureFlightAirline}
                        onChange={(e) => handleChange(index, "departureFlightAirline", e.target.value)}
                        className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <input
                        type="text"
                        placeholder="Departure Flight Number"
                        value={guest.departureFlightNumber}
                        onChange={(e) => handleChange(index, "departureFlightNumber", e.target.value)}
                        className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                </div>
            </div>
        ))}

        <button
            onClick={handleSubmit}
            disabled={isLoading}
            className={`w-full bg-green-500 text-white py-3 rounded-lg font-semibold shadow-md hover:bg-green-600 transition-all ${
                isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
        >
            {isLoading ? "Updating..." : "Update Guest Details"}
        </button>
    </div>
</div>

    );
};

export default UpdateGuestDetailsForm;
